import { ROTULO_GATILHO, fluxoFormSchema, type FluxoForm, type FluxoResumo } from '@mymaxx/shared';
import { Aviso, Botao, CampoTexto, Modal } from '@mymaxx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleDot, Pencil, Plus, Trash2, Workflow } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ErroApi } from '../lib/api';
import { fluxosApi } from '../lib/fluxos-api';

const FORM_VAZIO: FluxoForm = { nome: '', descricao: '', gatilho: 'entrada', ativo: true };

/** Lista de fluxos: onde cada um é usado, se está no ar e se tem alterações pendentes. */
export function Fluxos() {
  const queryClient = useQueryClient();
  const navegar = useNavigate();
  const [editando, setEditando] = useState<{ id: string | null; form: FluxoForm } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const fluxos = useQuery({ queryKey: ['fluxos'], queryFn: fluxosApi.listar });
  const recarregar = () => void queryClient.invalidateQueries({ queryKey: ['fluxos'] });
  const tratarErro = (e: unknown) =>
    setErro(e instanceof ErroApi ? e.mensagem : 'Não foi possível concluir');

  const salvar = useMutation({
    mutationFn: ({ id, form }: { id: string | null; form: FluxoForm }) =>
      id ? fluxosApi.atualizar(id, form) : fluxosApi.criar(form),
    onSuccess: (fluxo, variaveis) => {
      recarregar();
      setEditando(null);
      setErro(null);
      // Fluxo novo já abre no editor: o próximo passo é desenhar.
      if (!variaveis.id) navegar(`/fluxos/${fluxo.id}`);
    },
    onError: tratarErro,
  });

  const excluir = useMutation({
    mutationFn: (id: string) => fluxosApi.excluir(id),
    onSuccess: recarregar,
    onError: tratarErro,
  });

  function enviar() {
    if (!editando) return;
    const parse = fluxoFormSchema.safeParse(editando.form);
    if (!parse.success) {
      setErro(parse.error.issues[0]?.message ?? 'Confira os campos');
      return;
    }
    salvar.mutate({ id: editando.id, form: parse.data });
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Fluxos de atendimento</h1>
          <p className="text-sm text-neutral-500">
            O atendimento automático que recebe o cliente, identifica e encaminha ao setor.
          </p>
        </div>
        <Botao
          onClick={() => {
            setEditando({ id: null, form: { ...FORM_VAZIO } });
            setErro(null);
          }}
        >
          <Plus size={16} /> Novo fluxo
        </Botao>
      </header>

      {erro && !editando && <Aviso tipo="erro">{erro}</Aviso>}
      {fluxos.isPending && <p className="text-sm text-neutral-500">Carregando…</p>}

      {fluxos.data?.length === 0 && (
        <div className="rounded-card border border-dashed border-neutral-300 px-4 py-10 text-center dark:border-neutral-700">
          <Workflow className="mx-auto mb-2 text-neutral-400" size={28} />
          <p className="text-sm text-neutral-500">
            Nenhum fluxo ainda. Crie o primeiro para montar a triagem de entrada.
          </p>
        </div>
      )}

      <ul className="grid gap-3 md:grid-cols-2">
        {fluxos.data?.map((f: FluxoResumo) => (
          <li
            key={f.id}
            className={`rounded-card border p-4 ${
              f.ativo
                ? 'border-neutral-200 dark:border-neutral-800'
                : 'border-dashed border-neutral-300 opacity-70 dark:border-neutral-700'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Link to={`/fluxos/${f.id}`} className="font-medium hover:underline">
                  {f.nome}
                </Link>
                {f.descricao && <p className="text-sm text-neutral-500">{f.descricao}</p>}
              </div>
              <span className="shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                {ROTULO_GATILHO[f.gatilho]}
              </span>
            </div>

            <div className="mt-3 space-y-1 text-xs">
              <p className="flex items-center gap-1.5">
                <CircleDot
                  size={12}
                  className={f.versaoPublicada ? 'text-green-600' : 'text-neutral-400'}
                />
                {f.versaoPublicada
                  ? `Versão ${f.versaoPublicada} no ar desde ${new Date(f.publicadaEm!).toLocaleDateString('pt-BR')}`
                  : 'Nunca publicado'}
              </p>
              {f.temAlteracoesNaoPublicadas && (
                <p className="text-amber-700 dark:text-amber-400">
                  Há alterações no rascunho ainda não publicadas.
                </p>
              )}
              <p className="text-neutral-500">
                {f.usadoPor.length === 0
                  ? 'Não vinculado a nenhum setor'
                  : `Usado por: ${f.usadoPor.map((u) => `${u.setorNome} (${u.papel === 'entrada' ? 'entrada' : 'fora do horário'})`).join(', ')}`}
              </p>
            </div>

            <div className="mt-3 flex gap-2">
              <Link to={`/fluxos/${f.id}`}>
                <Botao>
                  <Workflow size={14} /> Abrir editor
                </Botao>
              </Link>
              <Botao
                variante="secundario"
                onClick={() =>
                  setEditando({
                    id: f.id,
                    form: {
                      nome: f.nome,
                      descricao: f.descricao ?? '',
                      gatilho: f.gatilho,
                      ativo: f.ativo,
                    },
                  })
                }
              >
                <Pencil size={14} />
              </Botao>
              <Botao
                variante="secundario"
                onClick={() => {
                  if (confirm(`Excluir o fluxo ${f.nome}?`)) excluir.mutate(f.id);
                }}
              >
                <Trash2 size={14} />
              </Botao>
            </div>
          </li>
        ))}
      </ul>

      <Modal
        aberto={!!editando}
        titulo={editando?.id ? 'Editar fluxo' : 'Novo fluxo'}
        descricao={editando?.id ? undefined : 'Depois de criar, você desenha o fluxo no editor.'}
        onFechar={() => setEditando(null)}
        acoes={
          <>
            <Botao variante="secundario" onClick={() => setEditando(null)}>
              Cancelar
            </Botao>
            <Botao onClick={enviar} carregando={salvar.isPending}>
              Salvar
            </Botao>
          </>
        }
      >
        {editando && (
          <div className="space-y-4">
            <CampoTexto
              rotulo="Nome"
              autoFocus
              value={editando.form.nome}
              onChange={(e) =>
                setEditando({ ...editando, form: { ...editando.form, nome: e.target.value } })
              }
            />
            <CampoTexto
              rotulo="Descrição"
              value={editando.form.descricao ?? ''}
              onChange={(e) =>
                setEditando({ ...editando, form: { ...editando.form, descricao: e.target.value } })
              }
            />
            <label className="block text-sm font-medium">
              Quando este fluxo roda
              <select
                value={editando.form.gatilho}
                onChange={(e) =>
                  setEditando({
                    ...editando,
                    form: { ...editando.form, gatilho: e.target.value as FluxoForm['gatilho'] },
                  })
                }
                className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              >
                {Object.entries(ROTULO_GATILHO).map(([valor, rotulo]) => (
                  <option key={valor} value={valor}>
                    {rotulo}
                  </option>
                ))}
              </select>
              <span className="block text-xs font-normal text-neutral-500">
                Depois, vincule o fluxo ao setor na tela de Setores.
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editando.form.ativo}
                onChange={(e) =>
                  setEditando({ ...editando, form: { ...editando.form, ativo: e.target.checked } })
                }
              />
              Fluxo ativo
            </label>
            {erro && <Aviso tipo="erro">{erro}</Aviso>}
          </div>
        )}
      </Modal>
    </div>
  );
}
